<template>
    <div class="el-form-item" :class="itemClass">
        <label
            v-if="label || $slots.label"
            :for="labelFor"
            class="el-form-item__label"
        >
            <slot name="label">{{ label || $slots.label }}</slot>
        </label>
        <div class="el-form-item__content">
            <slot
                ref="inputComponent"
                :aria-invalid="invalid"
                :aria-label="label || $slots.label || ''"
            />
            <div
                v-show="displayErrorAlways || isInvalidMessageShown"
                class="el-form-item__error-holder"
            >
                <div v-if="isInvalidMessageShown" class="el-form-item__error">
                    <el-form-error
                        v-for="(message, index) in errorMessages"
                        :key="`message-${index}`"
                    >
                        {{ message }}
                    </el-form-error>
                </div>
                <div
                    v-else-if="hintText"
                    class="el-form-item__hint el-text--caption el-text--ellipsis el-text--color-fixed-light"
                >
                    {{ hintText }}
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { isEqual } from 'lodash-es';
import { computed, ref, inject, watch, onMounted, onBeforeUnmount, provide, nextTick } from 'vue';
import ElFormError from './form-error.vue';

let AsyncValidator = null;
const AsyncValidatorImport = () => import('async-validator');

const emit = defineEmits(['status']);

const props = defineProps({
    label: {
        type: String,
        default: '',
    },
    prop: {
        type: String,
        required: true,
    },
    required: {
        type: Boolean,
        default: false,
    },
    rules: {
        type: [Object, Array],
        default: () => [],
    },
    error: {
        type: [String, Array, Boolean],
        default: '',
    },
    inlineMessage: {
        type: [Boolean, String],
        default: '',
    },
    validateStatus: {
        type: String,
        default: '',
    },
    for: {
        type: String,
        default: '',
    },
    showMessage: {
        type: Boolean,
        default: true,
    },
    dynamic: {
        type: Boolean,
        default: false,
    },
    displayErrorAlways: {
        type: Boolean,
        default: true,
    },
    hint: {
        type: String,
        default: '',
    },
    ariaDescribedby: {
        type: String,
        default: '',
    },
});

const elForm = inject('elForm', null);
const validateDisabledByChange = ref(false);
const validateDisabledByInput = ref(false);

const initialValue = ref('');
const errorMessage = ref('');
const errorMessages = ref([]);
const validateState = ref('');
const hasValidated = ref(false);
const touched = ref(false);
const isFocused = ref(false);
const inputComponent = ref(null);

const modelValue = computed(() => {
    if (!elForm?.model || !props.prop) return undefined;
    const path = props.prop.replace(/:/, '.');
    return getPropByPath(elForm.model, path, !props.dynamic).v;
});

const dirty = computed(() => {
    if (Array.isArray(initialValue.value)) {
        return JSON.stringify(initialValue.value) !== JSON.stringify(modelValue.value);
    }
    if (typeof initialValue.value === 'object') {
        return !isEqual(initialValue.value, modelValue.value);
    }
    return initialValue.value !== modelValue.value;
});

const isEmpty = computed(() =>
    isRequired.value &&
    (modelValue.value === '' ||
    (Array.isArray(modelValue.value) &&
    modelValue.value.length === 0)),
);

const isValidating = computed(() => validateState.value === 'validating');
const valid = computed(() => validateState.value === 'success');
const invalid = computed(() => validateState.value === 'error');
const isRequired = computed(() => allRules.value.some(r => r.required));
const labelFor = computed(() => props.for || props.prop);

const isInvalidMessageShown = computed(() =>
    validateState.value === 'error' && props.showMessage && elForm?.showMessage,
);

const isErrorInHolder = computed(() =>
    typeof props.inlineMessage === 'boolean'
        ? props.inlineMessage
        : (elForm?.inlineMessage || false),
);

const allRules = computed(() => {
    const formRules = elForm?.rules ? getRulesByPath(elForm.rules, props.prop || '') : [];
    const selfRules = props.rules || [];
    const requiredRule = props.required ? [{ required: Boolean(props.required) }] : [];
    return [].concat(selfRules, formRules, requiredRule).filter(Boolean);
});

const hintText = computed(() =>
    props.hint || elForm?.hints?.[props.prop || labelFor.value] || '',
);

const itemClass = computed(() => ({
    'is-required': isRequired.value || props.required,
    'is-error-absolute': !isErrorInHolder.value,
    'is-validating': isValidating.value,
    'is-success': valid.value && hasValidated.value && !invalid.value,
    'is-error': invalid.value,
    'is-touched': touched.value,
    'is-focus': isFocused.value,
}));

function getPropByPath(obj, path, strict = false) {
    if (typeof path !== 'string') {
        throw new Error('Path must be a string');
    }

    const normalizedPath = path.replace(/\[(\w+)\]/g, '.$1').replace(/^\./, '');
    const keys = normalizedPath.split('.');
    let tempObj = obj;

    for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (tempObj && Object.prototype.hasOwnProperty.call(tempObj, key)) {
            tempObj = tempObj[key];
        } else {
            if (strict) {
                throw new Error('Invalid prop path');
            }
            tempObj = null;
            break;
        }
    }

    const lastKey = keys[keys.length - 1];
    return {
        o: tempObj,
        k: lastKey,
        v: tempObj?.[lastKey] ?? null,
    };
}

function objectAssign(target, ...sources) {
    for (const source of sources) {
        if (!source) continue;

        for (const key of Object.keys(source)) {
            const value = source[key];
            if (value !== undefined) {
                // eslint-disable-next-line no-param-reassign
                target[key] = value;
            }
        }
    }

    return target;
}


// Manual error injection
watch(() => props.error, (val) => {
    if (typeof val === 'boolean') {
        errorMessages.value = [];
        errorMessage.value = '';
        validateState.value = val ? 'error' : '';
    } else if (Array.isArray(val)) {
        errorMessages.value = val;
        errorMessage.value = val[0] || '';
        validateState.value = val.length ? 'error' : '';
    } else {
        errorMessages.value = val ? [val] : [];
        errorMessage.value = val;
        validateState.value = val ? 'error' : '';
    }
}, { immediate: true });

watch(() => props.validateStatus, (val) => {
    validateState.value = val;
});

// Status propagation
const statusMap = { touched, dirty, valid, invalid, isEmpty, isValidating };

Object.entries(statusMap).forEach(([key, source]) => {
    watch(source, (val) => {
        emit('status', { name: key, value: val });
    }, { immediate: true });
});

const publicMethods = {
    prop: props.prop,
    validate,
    clearValidate,
    resetField,
    resetInitialValue,
    focus: () => inputComponent.value?.focus?.(),
    touched: computed(() => touched.value),
    dirty: computed(() => dirty.value),
    valid: computed(() => valid.value),
    invalid: computed(() => invalid.value),
    isEmpty: computed(() => isEmpty.value),
    isValidating: computed(() => isValidating.value),
    validateState: computed(() => validateState.value),
};


onMounted(async () => {
    const AsyncValidatorPackage = await AsyncValidatorImport();
    AsyncValidator = AsyncValidatorPackage.default;

    if (props.prop && elForm) {
        nextTick(() => {
            elForm.addField(publicMethods);
            resetInitialValue();
        });
    }
});

onBeforeUnmount(() => {
    elForm?.removeField(publicMethods);
});

function getFilteredRule(trigger, isSubmit = false) {
    if (isSubmit) {
        return allRules.value.map(rule => objectAssign({}, rule));
    }

    return allRules.value
        .filter(rule => {
            if (rule.trigger == null) return true;
            if (Array.isArray(rule.trigger)) return rule.trigger.includes(trigger);
            if (typeof rule.trigger === 'string' && rule.trigger.includes(' ')) {
                return rule.trigger.split(' ').includes(trigger);
            }
            return rule.trigger === trigger;
        })
        .map(rule => objectAssign({}, rule));
}

function getRulesByPath(rules, prop = '') {
    const paths = prop.split('.');
    let current = rules;
    for (let i = 0, j = paths.length; i < j; i++) {
        const path = paths[i];
        if (!current) break;

        if (current[path] !== undefined) {
            current = current[path];
        } else if (current.fields && typeof current.fields === 'object' && current.fields[path] !== undefined) {
            current = current.fields[path];
        } else {
            current = current.defaultField;
        }
    }
    return current || null;
}

function validate(trigger = '', callback = () => {}, isSubmit = false) {
    // Avoid flacky tests
    // TODO: this is a quick & dirty solution.
    // Instead, we must make this method async and wait for the import
    // until the AsyncValidator constructor is ready.
    // Can't do it right now because I have no idea if this method callers are ok with changing it to async,
    // even though they should be, given there is a callback in the args.
    // eslint-disable-next-line no-underscore-dangle
    if (typeof window !== 'undefined' && window.__vitest_browser__ && !AsyncValidator) {
        return false;
    }

    const rules = getFilteredRule(trigger, isSubmit);
    // Legacy fallback: handle manual error prop if no rules defined
    if ((!rules || rules.length === 0)) {
        if (typeof props.error === 'boolean' && props.error) {
            errorMessages.value = [];
            errorMessage.value = '';
            validateState.value = 'error';
        } else if (Array.isArray(props.error) && props.error.length > 0) {
            errorMessages.value = props.error;
            errorMessage.value = props.error[0];
            validateState.value = 'error';
        } else if (props.error) {
            errorMessages.value = [props.error];
            errorMessage.value = props.error;
            validateState.value = 'error';
        }
        callback?.(errorMessage.value);
        return true;
    }

    // Reset errors unless in error/validating
    if (validateState.value !== 'error' && validateState.value !== 'validating') {
        validateState.value = '';
        errorMessages.value = [];
        errorMessage.value = '';
    }

    // The async-validator chunk is imported lazily in onMounted. Early input/change
    // events (e.g. when a form opens with prefilled values) can call validate() before
    // the constructor is ready — skip until it loads, submit re-validates anyway.
    if (!AsyncValidator) {
        validateState.value = '';
        return false;
    }

    validateState.value = 'validating';

    const descriptor = { [props.prop]: rules.map(r => ({ ...r, trigger: undefined })) };
    const validator = new AsyncValidator(descriptor);
    const model = { [props.prop]: modelValue.value };

    validator.validate(model, { firstFields: true }, (errors) => {
        if (errors) {
            validateState.value = 'error';
            errorMessages.value = errors.map(err => err.message);
            errorMessage.value = errorMessages.value[0] || '';
        } else {
            validateState.value = 'success';
            errorMessages.value = [];
            errorMessage.value = '';
        }

        hasValidated.value = true;
        callback?.(errorMessage.value);
    });
}

function clearValidate() {
    validateState.value = '';
    errorMessages.value = [];
    errorMessage.value = '';
}

function resetField() {
    validateDisabledByChange.value = true;
    validateDisabledByInput.value = true;

    touched.value = false;
    clearValidate();
    const path = props.prop.replace(/:/, '.');
    const propVal = getPropByPath(elForm.model, path, true);
    propVal.o[propVal.k] = Array.isArray(initialValue.value)
        ? [...initialValue.value]
        : initialValue.value;
}

function resetInitialValue() {
    initialValue.value = Array.isArray(modelValue.value)
        ? [...modelValue.value]
        : modelValue.value;
}

// Contexts
provide('elFormItem', {
    prop: props.prop,
    validate,
    clearValidate,
    resetField,
    markAsFocused: () => { isFocused.value = true; },
    markAsBlurred: () => { isFocused.value = false; },
    markAsTouched: () => { touched.value = true; },
});
provide('elFormItemIsInvalid', () => invalid.value);
provide('elFormItemRegisterInput', (comp) => {
    inputComponent.value = comp;
});

// Expose public methods
defineExpose({
    focus: () => inputComponent.value?.focus?.(),
    validate,
    clearValidate,
    resetField,
    resetInitialValue,
});
</script>

<style lang="postcss" scoped>
.el-form-item {
    padding-bottom: 8px;
    font-family: var(--font-family-default);
    @mixin body;
    color: var(--av-fixed-primary);
    display: flex;
    flex-direction: column;
    user-select: none;
    &__label {
        line-height: 32px;
    }
    &__content {
        position: relative;
    }
    &__error-holder {
        min-height: 16px;
    }
    &__error {
        @mixin caption;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        color: var(--av-fixed-danger);
        white-space: normal;
        word-break: break-all;
        user-select: none;
    }
    &.is-error.is-focus {
        .el-form-item__error {
            color: var(--av-fixed-light);
        }
    }
}
</style>
